<script setup lang="ts">
import SuccessIcon from '~/assets/icon/success.svg'
import ErrorIcon from '~/assets/icon/error.svg'
import type { AppToastType } from '~/types/toast'

const props = withDefaults(
  defineProps<{
    type: AppToastType
    message: string
    duration?: number
    zIndex?: number
    context?: 'default' | 'usercenter'
    variant?: 'default' | 'figma-copy'
  }>(),
  {
    duration: 3000,
    zIndex: 9999,
    context: 'default',
    variant: 'default',
  },
)

const emit = defineEmits<{
  close: []
}>()

const visible = ref(false)
let closeTimer: ReturnType<typeof setTimeout> | null = null

const isSuccess = computed(() => props.type === 1 || props.type === 'success')
const iconComponent = computed(() => (isSuccess.value ? SuccessIcon : ErrorIcon))

const clearCloseTimer = () => {
  if (!closeTimer) return
  clearTimeout(closeTimer)
  closeTimer = null
}

const showToast = () => {
  clearCloseTimer()
  visible.value = true

  if (props.duration <= 0) return

  closeTimer = setTimeout(() => {
    visible.value = false
  }, props.duration)
}

watch(
  () => [props.type, props.message, props.duration],
  () => {
    showToast()
  },
)

onMounted(() => {
  requestAnimationFrame(showToast)
})

onBeforeUnmount(() => {
  clearCloseTimer()
})
</script>

<template>
  <Transition name="app-toast" @after-leave="emit('close')">
    <div
      v-if="visible"
      class="app-toast-wrap"
      :class="{ 'app-toast-wrap--usercenter': props.context === 'usercenter' }"
      :style="{ zIndex }"
    >
      <div
        class="app-toast"
        :class="{ 'app-toast--figma-copy': props.variant === 'figma-copy' }"
        role="status"
        aria-live="polite"
      >
        <component :is="iconComponent" class="app-toast__icon" />
        <span class="app-toast__message">{{ message }}</span>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="less">
.app-toast-wrap {
  position: fixed;
  top: max(96px, 15vh);
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
}

.app-toast-wrap--usercenter {
  /* 用户中心页面保持原本靠近内容区顶部的视觉位置，同时 fixed 到视口避免滚出屏幕 */
  top: 117px;
}

.app-toast {
  min-width: 130px;
  min-height: 46px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid rgba(11, 18, 40, 0.14);
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 16px 32px rgba(5, 21, 36, 0.16);
}

/* 复制成功提示严格对齐 Figma 节点 6351:75646，避免继承通用 Toast 的最小宽度和阴影。 */
.app-toast--figma-copy {
  min-width: 0;
  min-height: 0;
  border-width: 0.5px;
  box-shadow: none;
}

.app-toast__icon {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
}

.app-toast__message {
  color: rgba(41, 42, 46, 1);
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  white-space: nowrap;
}

.app-toast-enter-active,
.app-toast-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.app-toast-enter-from,
.app-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -12px);
}
</style>
